import { useEffect, useState } from 'react';
import { getSocket } from './socket.js';

export interface AuthUser {
  id: string;
  username: string;
  displayName: string;
  role: 'admin' | 'producer';
  active: boolean;
}

async function fetchMe(): Promise<AuthUser | null> {
  try {
    const res = await fetch('/api/auth/me', { credentials: 'include' });
    if (!res.ok) return null;
    const data = (await res.json()) as { user: AuthUser };
    return data.user;
  } catch {
    return null;
  }
}

/** Re-handshake the socket so it carries the (new/cleared) auth cookie. */
function reconnectSocket(): void {
  const s = getSocket();
  s.disconnect();
  s.connect();
}

export async function login(
  username: string,
  password: string,
): Promise<{ ok: true; user: AuthUser } | { ok: false; error: string }> {
  try {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    const data = (await res.json()) as { user?: AuthUser; error?: string };
    if (!res.ok || !data.user) return { ok: false, error: data.error ?? 'Login failed' };
    reconnectSocket();
    return { ok: true, user: data.user };
  } catch {
    return { ok: false, error: 'Network error' };
  }
}

export async function logout(): Promise<void> {
  try {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
  } catch {
    /* ignore */
  }
  reconnectSocket();
}

/** undefined = still loading; null = not logged in; AuthUser = logged in. */
export function useAuth() {
  const [user, setUser] = useState<AuthUser | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    void fetchMe().then((u) => {
      if (alive) setUser(u);
    });
    return () => {
      alive = false;
    };
  }, []);
  return {
    user,
    loading: user === undefined,
    setUser,
    refresh: () => fetchMe().then(setUser),
  };
}
